import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'primereact/tooltip';
import PropTypes from 'prop-types';

import { getAcadamicLevelShortHand, getContactOrganizationName } from 'utils/func';
import DownloadScientificProfile from 'features/ProjectProposal/components/DownloadScientificProfile';
import Warning from 'features/ProjectProposal/components/Warning';

export default function PreResearchersTable({
  organizations,
  watch,
  control,
  handleDownloadScientificProfileFile,
}) {
  // #region Data
  const { t } = useTranslation();

  const owner = watch('preGeneral.owner');

  const { fields: scientificSecretaryFields } = useFieldArray({
    control,
    name: 'preGeneral.scientificSecretaries',
  });

  const { fields: mainParticipantFields } = useFieldArray({
    control,
    name: 'preGeneral.mainParticipants',
  });

  const { fields: participantFields } = useFieldArray({
    control,
    name: 'preGeneral.participants',
  });

  const { fields: technicianFields } = useFieldArray({
    control,
    name: 'preGeneral.technicians',
  });

  const { fields: domesticExpertFields } = useFieldArray({
    control,
    name: 'preGeneral.domesticExperts',
  });

  const { fields: internationalExpertFields } = useFieldArray({
    control,
    name: 'preGeneral.internationalExperts',
  });
  // #endregion Data

  // #region Event
  // #endregion Event

  return (
    <div className="form-table">
      <table className="mb-2">
        <thead>
          <tr>
            <th className="text-center" style={{ minWidth: 50, width: 50 }}>
              {t('formLayout.numericalOrder')}
            </th>
            <th className="text-center" style={{ minWidth: 200, width: '30%' }}>
              {`${t('projectProposal.general.researchHumanResources.fields.academicRank')}, ${t(
                'projectProposal.general.researchHumanResources.fields.academicDegree'
              )}, ${t('projectProposal.general.researchHumanResources.fields.name')}`}
            </th>
            <th className="text-center" style={{ minWidth: 100, width: '5%' }}>
              {t('projectProposal.general.researchHumanResources.fields.gender')}
            </th>
            <th className="text-center" style={{ minWidth: 200, width: '30%' }}>
              {t('projectProposal.general.researchHumanResources.fields.organization')}
            </th>
            <th className="text-center" style={{ minWidth: 200, width: '15%' }}>
              {t('projectProposal.general.researchHumanResources.fields.assignedTask')}
            </th>
            <th className="text-center" style={{ minWidth: 200, width: '15%' }}>
              {t('projectProposal.general.researchHumanResources.fields.reason')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="font-bold">
            <td colSpan={6}>
              <div>
                <span>{t('projectProposal.general.researchHumanResources.ownerFields.label')}</span>
              </div>
            </td>
          </tr>
          <tr>
            <td className="text-center">1</td>
            <td>
              {`${getAcadamicLevelShortHand(owner?.academicRank, owner?.academicDegree, t, true)}${
                owner?.fullname
              }`}
              {owner?.scientificProfileUrl ? (
                <DownloadScientificProfile
                  contactId={owner?.id || owner?.contact?.id || owner?.contactId}
                  contactName={owner?.fullname}
                  academicRank={owner?.academicRank}
                  academicDegree={owner?.academicDegree}
                  handleDownloadFile={handleDownloadScientificProfileFile}
                />
              ) : (
                <Warning
                  message={t(
                    'projectProposal.general.researchHumanResources.noScientificProfileUrl'
                  )}
                  tooltipTarget="researcher-owner"
                />
              )}
            </td>
            <td className="text-center">{t(`userProfile.gender.${owner?.gender}`)}</td>
            <td>
              {getContactOrganizationName(
                organizations,
                {
                  organizationId: owner?.organizationId,
                  specificOrganizationName: owner?.specificOrganizationName,
                },
                t
              )}
            </td>
            <td>{owner?.assignedTask}</td>
            <td>{owner?.reason}</td>
          </tr>

          <tr className="font-bold">
            <td colSpan={6}>
              <div>
                <span>
                  {t(
                    'projectProposal.general.researchHumanResources.scientificSecretaryFields.label'
                  )}
                </span>
              </div>
            </td>
          </tr>
          {scientificSecretaryFields?.map((item, index) => (
            <tr key={item.id}>
              <td className="text-center">{index + 1}</td>
              <td>
                {`${getAcadamicLevelShortHand(item?.academicRank, item?.academicDegree, t, true)}${
                  item?.fullname
                }`}
                {item?.contact?.user?.scientificProfileUrl ? (
                  <DownloadScientificProfile
                    contactId={item?.contact?.id || item?.contactId}
                    contactName={item?.fullname}
                    academicRank={item?.academicRank}
                    academicDegree={item?.academicDegree}
                    handleDownloadFile={handleDownloadScientificProfileFile}
                  />
                ) : (
                  <Warning
                    message={t(
                      'projectProposal.general.researchHumanResources.noScientificProfileUrl'
                    )}
                    tooltipTarget={`researcher-scientific-secretary-${index}`}
                  />
                )}
                {item?.isAuthorized && (
                  <>
                    <i
                      className="tick-icon pi pi-pencil border-primary-900 ml-2"
                      data-pr-tooltip={t(
                        'projectProposal.general.researchHumanResources.fields.authority'
                      )}
                      data-pr-position="right"
                    />
                    <Tooltip target=".tick-icon" />
                  </>
                )}
              </td>
              <td className="text-center">{t(`userProfile.gender.${item?.gender}`)}</td>
              <td>
                {getContactOrganizationName(
                  organizations,
                  {
                    organizationId: item?.organizationId,
                    specificOrganizationName: item?.specificOrganizationName,
                  },
                  t
                )}
              </td>
              <td>{item?.assignedTask}</td>
              <td>{item?.reason}</td>
            </tr>
          ))}
          <tr className="font-bold">
            <td colSpan={6}>
              <div>
                <span>
                  {t('projectProposal.general.researchHumanResources.mainParticipantFields.label')}
                </span>
              </div>
            </td>
          </tr>
          {mainParticipantFields?.map((item, index) => (
            <tr key={item.id}>
              <td className="text-center">{index + 1}</td>
              <td>
                {`${getAcadamicLevelShortHand(item?.academicRank, item?.academicDegree, t, true)}${
                  item?.fullname
                }`}
                {item?.contact?.user?.scientificProfileUrl ? (
                  <DownloadScientificProfile
                    contactId={item?.contact?.id || item?.contactId}
                    contactName={item?.fullname}
                    academicRank={item?.academicRank}
                    academicDegree={item?.academicDegree}
                    handleDownloadFile={handleDownloadScientificProfileFile}
                  />
                ) : (
                  <Warning
                    message={t(
                      'projectProposal.general.researchHumanResources.noScientificProfileUrl'
                    )}
                    tooltipTarget={`researcher-main-participant-${index}`}
                  />
                )}
                {item?.isAuthorized && (
                  <>
                    <i
                      className="tick-icon pi pi-pencil border-primary-900 ml-2"
                      data-pr-tooltip={t(
                        'projectProposal.general.researchHumanResources.fields.authority'
                      )}
                      data-pr-position="right"
                    />
                    <Tooltip target=".tick-icon" />
                  </>
                )}
              </td>
              <td className="text-center">{t(`userProfile.gender.${item?.gender}`)}</td>
              <td>
                {getContactOrganizationName(
                  organizations,
                  {
                    organizationId: item?.organizationId,
                    specificOrganizationName: item?.specificOrganizationName,
                  },
                  t
                )}
              </td>
              <td>{item?.assignedTask}</td>
              <td>{item?.reason}</td>
            </tr>
          ))}

          <tr className="font-bold">
            <td colSpan={6}>
              <div>
                <span>
                  {t('projectProposal.general.researchHumanResources.participantFields.label')}
                </span>
              </div>
            </td>
          </tr>
          {participantFields?.map((item, index) => (
            <tr key={item.id}>
              <td className="text-center">{index + 1}</td>
              <td>
                {`${getAcadamicLevelShortHand(item?.academicRank, item?.academicDegree, t, true)}${
                  item?.fullname
                }`}
                {item?.contact?.user?.scientificProfileUrl ? (
                  <DownloadScientificProfile
                    contactId={item?.contact?.id || item?.contactId}
                    contactName={item?.fullname}
                    academicRank={item?.academicRank}
                    academicDegree={item?.academicDegree}
                    handleDownloadFile={handleDownloadScientificProfileFile}
                  />
                ) : (
                  <Warning
                    message={t(
                      'projectProposal.general.researchHumanResources.noScientificProfileUrl'
                    )}
                    tooltipTarget={`researcher-participant-${index}`}
                  />
                )}
                {item?.isAuthorized && (
                  <>
                    <i
                      className="tick-icon pi pi-pencil border-primary-900 ml-2"
                      data-pr-tooltip={t(
                        'projectProposal.general.researchHumanResources.fields.authority'
                      )}
                      data-pr-position="right"
                    />
                    <Tooltip target=".tick-icon" />
                  </>
                )}
              </td>
              <td className="text-center">{t(`userProfile.gender.${item?.gender}`)}</td>
              <td>
                {getContactOrganizationName(
                  organizations,
                  {
                    organizationId: item?.organizationId,
                    specificOrganizationName: item?.specificOrganizationName,
                  },
                  t
                )}
              </td>
              <td>{item?.assignedTask}</td>
              <td>{item?.reason}</td>
            </tr>
          ))}
          <tr className="font-bold">
            <td colSpan={6}>
              <div>
                <span>
                  {t('projectProposal.general.researchHumanResources.technicianFields.label')}
                </span>
              </div>
            </td>
          </tr>
          {technicianFields?.map((item, index) => (
            <tr key={item.id}>
              <td className="text-center">{index + 1}</td>
              <td>
                {`${getAcadamicLevelShortHand(item?.academicRank, item?.academicDegree, t, true)}${
                  technicianFields[index]?.fullname
                }`}
                {technicianFields[index]?.contact?.user?.scientificProfileUrl ? (
                  <DownloadScientificProfile
                    contactId={item?.contact?.id || item?.contactId}
                    contactName={technicianFields[index]?.fullname}
                    academicRank={item?.academicRank}
                    academicDegree={item?.academicDegree}
                    handleDownloadFile={handleDownloadScientificProfileFile}
                  />
                ) : (
                  <Warning
                    message={t(
                      'projectProposal.general.researchHumanResources.noScientificProfileUrl'
                    )}
                    tooltipTarget={`researcher-technician-${index}`}
                  />
                )}
                {item?.isAuthorized && (
                  <>
                    <i
                      className="tick-icon pi pi-pencil border-primary-900 ml-2"
                      data-pr-tooltip={t(
                        'projectProposal.general.researchHumanResources.fields.authority'
                      )}
                      data-pr-position="right"
                    />
                    <Tooltip target=".tick-icon" />
                  </>
                )}
              </td>
              <td className="text-center">{t(`userProfile.gender.${item?.gender}`)}</td>
              <td>
                {getContactOrganizationName(
                  organizations,
                  {
                    organizationId: item?.organizationId,
                    specificOrganizationName: item?.specificOrganizationName,
                  },
                  t
                )}
              </td>
              <td>{technicianFields[index]?.assignedTask}</td>
              <td>{technicianFields[index]?.reason}</td>
            </tr>
          ))}

          <tr className="font-bold">
            <td colSpan={6}>
              <div>
                <span>
                  {t('projectProposal.general.researchHumanResources.domesticExpertFields.label')}
                </span>
              </div>
            </td>
          </tr>
          {domesticExpertFields?.map((item, index) => (
            <tr key={item.id}>
              <td className="text-center">{index + 1}</td>
              <td>
                {`${getAcadamicLevelShortHand(item?.academicRank, item?.academicDegree, t, true)}${
                  item?.fullname
                }`}
                {item?.contact?.user?.scientificProfileUrl ? (
                  <DownloadScientificProfile
                    contactId={item?.contact?.id || item?.contactId}
                    contactName={item?.fullname}
                    academicRank={item?.academicRank}
                    academicDegree={item?.academicDegree}
                    handleDownloadFile={handleDownloadScientificProfileFile}
                  />
                ) : (
                  <Warning
                    message={t(
                      'projectProposal.general.researchHumanResources.noScientificProfileUrl'
                    )}
                    tooltipTarget={`researcher-domestic-expert-${index}`}
                  />
                )}
                {item?.isAuthorized && (
                  <>
                    <i
                      className="tick-icon pi pi-pencil border-primary-900 ml-2"
                      data-pr-tooltip={t(
                        'projectProposal.general.researchHumanResources.fields.authority'
                      )}
                      data-pr-position="right"
                    />
                    <Tooltip target=".tick-icon" />
                  </>
                )}
              </td>
              <td className="text-center">{t(`userProfile.gender.${item?.gender}`)}</td>
              <td>
                {getContactOrganizationName(
                  organizations,
                  {
                    organizationId: item?.organizationId,
                    specificOrganizationName: item?.specificOrganizationName,
                  },
                  t
                )}
              </td>
              <td>{item?.assignedTask}</td>
              <td>{item?.reason}</td>
            </tr>
          ))}
          <tr className="font-bold">
            <td colSpan={6}>
              <div>
                <span>
                  {t(
                    'projectProposal.general.researchHumanResources.internationalExpertFields.label'
                  )}
                </span>
              </div>
            </td>
          </tr>
          {internationalExpertFields?.map((item, index) => (
            <tr key={item.id}>
              <td className="text-center">{index + 1}</td>
              <td>
                {`${getAcadamicLevelShortHand(item?.academicRank, item?.academicDegree, t, true)}${
                  item?.fullname
                }`}
                {item?.contact?.user?.scientificProfileUrl ? (
                  <DownloadScientificProfile
                    contactId={item?.contact?.id || item?.contactId}
                    contactName={item?.fullname}
                    academicRank={item?.academicRank}
                    academicDegree={item?.academicDegree}
                    handleDownloadFile={handleDownloadScientificProfileFile}
                  />
                ) : (
                  <Warning
                    message={t(
                      'projectProposal.general.researchHumanResources.noScientificProfileUrl'
                    )}
                    tooltipTarget={`researcher-international-expert-${index}`}
                  />
                )}
                {item?.isAuthorized && (
                  <>
                    <i
                      className="tick-icon pi pi-pencil border-primary-900 ml-2"
                      data-pr-tooltip={t(
                        'projectProposal.general.researchHumanResources.fields.authority'
                      )}
                      data-pr-position="right"
                    />
                    <Tooltip target=".tick-icon" />
                  </>
                )}
              </td>
              <td className="text-center">{t(`userProfile.gender.${item?.gender}`)}</td>
              <td>
                {getContactOrganizationName(
                  organizations,
                  {
                    organizationId: item?.organizationId,
                    specificOrganizationName: item?.specificOrganizationName,
                  },
                  t
                )}
              </td>
              <td>{item?.assignedTask}</td>
              <td>{item?.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

PreResearchersTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array,
  watch: PropTypes.func.isRequired,
  errors: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
  handleDownloadScientificProfileFile: PropTypes.func.isRequired,
};

PreResearchersTable.defaultProps = {
  organizations: [],
};
